import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, CheckCircle2, LogIn } from "lucide-react";
import { useLang } from "../LanguageContext";
import { formatApiError } from "../AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/token-valid/${token}`);
        setState(data.valid ? "valid" : "invalid");
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) return setError(t("setpw.mismatch"));
    setLoading(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password: pw });
      setState("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-page py-16 sm:py-24" data-testid="page-setpassword">
      <div className="max-w-md mx-auto">
        <img src="/logo.png" alt="École Pleine Nature" className="w-16 h-16 object-contain mx-auto mb-6" />
        {state === "checking" && (
          <p className="text-center text-ink-muted">{t("setpw.checking")}</p>
        )}
        {state === "invalid" && (
          <div className="card-soft p-8 text-center" data-testid="setpw-invalid">
            <p className="text-ink-muted mb-6">{t("setpw.invalid")}</p>
            <Link to="/espace-membres" className="btn-primary">{t("setpw.go_login")}</Link>
          </div>
        )}
        {state === "done" && (
          <div className="card-soft p-8 text-center" data-testid="setpw-success">
            <CheckCircle2 size={28} className="text-forest mx-auto mb-4" />
            <p className="text-ink mb-6">{t("setpw.success")}</p>
            <Link to="/espace-membres" className="btn-primary" data-testid="setpw-go-login">
              {t("setpw.go_login")} <LogIn size={16} />
            </Link>
          </div>
        )}
        {state === "valid" && (
          <>
            <h1 className="font-heading text-3xl font-medium text-ink text-center mb-2">{t("setpw.title")}</h1>
            <p className="text-center text-ink-muted mb-8">{t("setpw.intro")}</p>
            <form onSubmit={submit} className="card-soft p-7 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">{t("setpw.password")}</label>
                <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
                  className="field" placeholder="••••••••" data-testid="setpw-password" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">{t("setpw.confirm")}</label>
                <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="field" placeholder="••••••••" data-testid="setpw-confirm" />
              </div>
              {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 text-sm" data-testid="setpw-error">{error}</div>
              )}
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="setpw-submit">
                <Lock size={16} /> {t("setpw.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
